import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';

/**
 * PumpStatusBar - Shows pump running state and uptime/runtime
 * Light blue background strip below the hero panel
 *
 * Props aligned with ESP32 MQTT fields:
 * - pumpState: derived from pS (0=off, 1=running, 2=calibrating)
 * - uptimeSeconds: U (device uptime)
 * - currentRunTime: pRT (current run time, only when running)
 * - isCalibrated: pCal
 * - calibrationRunCount: pCR
 * - totalRunCount: pTR
 */
function PumpStatusBar({
    pumpState = 'monitoring', // 'running' | 'off' | 'monitoring' | 'calibrating'
    uptimeSeconds = 0,
    currentRunTime = 0,
    isCalibrated = false,
    calibrationRunCount = 0,
    totalRunCount = 0,
}) {
    const formatUptime = (totalSeconds) => {
        const days = Math.floor(totalSeconds / (3600 * 24));
        const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        return `${days} Days ${hours}h ${minutes}m ${seconds}s`;
    };

    const formatRunTime = (seconds) => {
        if (seconds < 60) {
            return `${Math.round(seconds)}s`;
        }
        const minutes = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        return `${minutes}m ${secs}s`;
    };

    const getStatusText = () => {
        switch (pumpState) {
            case 'running':
                return 'Pump running';
            case 'calibrating':
                return 'Calibrating';
            case 'off':
                return 'Pump off';
            default:
                return 'Monitoring';
        }
    };

    const getSubText = () => {
        if (pumpState === 'running' && currentRunTime > 0) {
            return `Run time: ${formatRunTime(currentRunTime)}`;
        }
        if (pumpState === 'calibrating') {
            return `Learning: ${calibrationRunCount}/5 runs`;
        }
        return formatUptime(uptimeSeconds);
    };

    const isRunning = pumpState === 'running';
    const isCalibrating = pumpState === 'calibrating';

    return (
        <View style={[
            styles.container,
            isCalibrating && styles.containerCalibrating
        ]}>
            <View style={styles.leftContent}>
                <Text style={styles.statusText}>{getStatusText()}</Text>
                <Text style={styles.uptimeText}>{getSubText()}</Text>
                {isCalibrated && totalRunCount > 0 && !isRunning && (
                    <Text style={styles.totalText}>Total runs: {totalRunCount}</Text>
                )}
            </View>
            <View style={styles.rightContent}>
                {isRunning && (
                    <FontAwesome name="bolt" size={20} color="#1a1a2e" style={styles.icon} />
                )}
                {isCalibrating && (
                    <FontAwesome name="refresh" size={20} color="#856404" style={styles.icon} />
                )}
                <View style={[
                    styles.indicator,
                    isRunning && styles.indicatorActive,
                    isCalibrating && styles.indicatorCalibrating
                ]}>
                    <View style={[
                        styles.indicatorInner,
                        isRunning && styles.indicatorInnerActive,
                        isCalibrating && styles.indicatorInnerCalibrating
                    ]} />
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#d4eef7',
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
    containerCalibrating: {
        backgroundColor: '#fff3cd',
    },
    leftContent: {
        flex: 1,
    },
    statusText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1a1a2e',
    },
    uptimeText: {
        fontSize: 14,
        color: '#5a5a7a',
        marginTop: 2,
    },
    totalText: {
        fontSize: 12,
        color: '#5a5a7a',
        marginTop: 2,
    },
    rightContent: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    icon: {
        marginRight: 12,
    },
    indicator: {
        width: 32,
        height: 32,
        borderRadius: 16,
        borderWidth: 3,
        borderColor: '#1a1a2e',
        justifyContent: 'center',
        alignItems: 'center',
    },
    indicatorActive: {
        borderColor: '#33cc33',
    },
    indicatorCalibrating: {
        borderColor: '#ffc107',
    },
    indicatorInner: {
        width: 16,
        height: 16,
        borderRadius: 8,
        backgroundColor: 'transparent',
    },
    indicatorInnerActive: {
        backgroundColor: '#33cc33',
    },
    indicatorInnerCalibrating: {
        backgroundColor: '#ffc107',
    },
});

export default PumpStatusBar;
